import { useEffect, useMemo, useState } from 'react'
import { fetchSites } from './api'
import { HEALTH_POLL_MS } from './constants'
import type { Site } from './types'

export interface Sites {
  list: Site[]
  byId: Record<string, Site>
  status: 'loading' | 'ok' | 'error'
}

/**
 * The sites this user may see, refreshed with the health poll. Sites are
 * added and removed while VMS runs, and a list read once kept polling a
 * removed site and showed an added one only after a reload. A failed refresh
 * keeps the last list, as health keeps its last answer.
 */
export function useSites(): Sites {
  const [list, setList] = useState<Site[]>([])
  const [status, setStatus] = useState<Sites['status']>('loading')

  useEffect(() => {
    let cancelled = false
    const load = () => {
      fetchSites()
        .then((next) => {
          if (cancelled) return
          // Kept when unchanged, so a poll that changes nothing re-renders nothing.
          setList((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next))
          setStatus('ok')
        })
        .catch(() => {
          if (!cancelled) setStatus((s) => (s === 'ok' ? s : 'error'))
        })
    }
    load()
    const t = setInterval(load, HEALTH_POLL_MS)
    return () => { cancelled = true; clearInterval(t) }
  }, [])

  const byId = useMemo(() => Object.fromEntries(list.map((s) => [s.id, s])), [list])
  return { list, byId, status }
}
